(ns replicant.dom
  (:require [replicant.alias :as alias]
            [replicant.asserts :as asserts]
            [replicant.core :as r]
            [replicant.env :as env]
            [replicant.errors :as errors]
            [replicant.protocols :as replicant]
            [replicant.transition :as transition]))

(defn ^:no-doc remove-listener [^js/EventTarget el event opt]
  (when-let [old-handler (some-> el .-replicantHandlers (aget event))]
    (.removeEventListener el event old-handler (clj->js opt))))

(defn ^:no-doc on-next-frame [f]
  (js/requestAnimationFrame
   #(js/requestAnimationFrame f)))

(defn ^:no-doc -on-transition-end [el f]
  (let [[n dur] (-> (js/window.getComputedStyle el)
                    (.getPropertyValue "transition-duration")
                    transition/get-transition-stats)]
    (if (= n 0)
      (f)
      (let [complete (volatile! 0)
            timer (volatile! nil)
            started (js/Date.)
            callback (fn listener [& _args]
                       (let [cn (vswap! complete inc)]
                         (when (or (<= n cn)
                                   (< dur (- (js/Date.) started)))
                           (.removeEventListener el "transitionend" listener)
                           (js/clearTimeout @timer)
                           (f))))]
        (.addEventListener el "transitionend" callback)
        ;; The timer is a fail-safe. You could have set transition properties
        ;; that either don't change, or don't change in a way that triggers an
        ;; actual transition on unmount (e.g. changing height from auto to 0
        ;; causes no transition). When this happens, there will not be as many
        ;; transitionend events as there are transition durations. To avoid
        ;; getting stuck, the timer will come in and clean up.
        ;;
        ;; The timer is set with a hefty delay to avoid cutting a transition
        ;; short, in the case of a backed up browser working on overtime. Not
        ;; sure how realistic this is, but better safe than sorry, and the
        ;; important part is that the element doesn't get stuck forever.
        (vreset! timer (js/setTimeout callback (+ dur 200)))))))

(def ^:no-doc memories (js/WeakMap.))

(defn ^:export recall [node]
  (.get ^js memories node))

(defn ^:no-doc create-renderer []
  (reify
    replicant/IRender
    (attached? [_this el]
      (.-isConnected el))

    (create-text-node [_this text]
      (js/document.createTextNode text))

    (create-element [_this tag-name options]
      (if-let [ns (:ns options)]
        (js/document.createElementNS ns tag-name)
        (js/document.createElement tag-name)))

    (set-style [this ^js el style v]
      (.setProperty (.-style el) (name style) v)
      this)

    (remove-style [this ^js el style]
      (.removeProperty (.-style el) (name style))
      this)

    (add-class [this ^js el cn]
      (.add (.-classList el) cn)
      this)

    (remove-class [this ^js el cn]
      (.remove (.-classList el) cn)
      this)

    (set-attribute [this ^js el attr v opt]
      (errors/with-error-handling "setting attribute" {:el el :attr attr :v v}
        (cond
          (= "innerHTML" attr)
          (set! (.-innerHTML el) v)

          (= "value" attr)
          (set! (.-value el) v)

          (= "default-value" attr)
          (.setAttribute el "value" v)

          (= "selected" attr)
          (set! (.-selected el) v)

          (= "default-selected" attr)
          (.setAttribute el "selected" v)

          (= "checked" attr)
          (set! (.-checked el) v)

          (= "default-checked" attr)
          (.setAttribute el "checked" v)

          (:ns opt)
          (.setAttributeNS el (:ns opt) attr v)

          :else
          (.setAttribute el attr v)))
      this)

    (remove-attribute [this ^js el attr]
      (cond
        (= "innerHTML" attr)
        (set! (.-innerHTML el) "")

        (= "value" attr)
        (set! (.-value el) nil)

        (= "default-value" attr)
        (.removeAttribute el "value")

        (= "selected" attr)
        (set! (.-selected el) nil)

        (= "default-selected" attr)
        (.removeAttribute el "selected")

        (= "checked" attr)
        (set! (.-checked el) nil)

        (= "default-checked" attr)
        (.removeAttribute el "checked")

        :else
        (.removeAttribute el attr))
      this)

    (set-event-handler [this ^js/EventTarget el event handler opt]
      (when-not (.-replicantHandlers el)
        (set! (.-replicantHandlers el) #js {}))
      (let [event (name event)]
        (remove-listener el event opt)
        (aset (.-replicantHandlers el) event handler)
        (.addEventListener el event handler (clj->js opt)))
      this)

    (remove-event-handler [this ^js/EventTarget el event opt]
      (let [event (name event)]
        (remove-listener el event opt)
        (aset (.-replicantHandlers el) event nil))
      this)

    (append-child [this ^js el child-node]
      (.appendChild el child-node)
      this)

    (insert-before [this ^js el child-node reference-node]
      (.insertBefore el child-node reference-node)
      this)

    (remove-child [this ^js el child-node]
      (.removeChild el child-node)
      this)

    (on-transition-end [this ^js el f]
      (-on-transition-end el f)
      this)

    (replace-child [this ^js el insert-child replace-child]
      (.replaceChild el insert-child replace-child)
      this)

    (remove-all-children [this ^js el]
      (set! (.-textContent el) "")
      this)

    (get-child [_this ^js el idx]
      (aget (.-childNodes el) idx))

    (next-frame [_this f]
      (on-next-frame f))

    replicant/IMemory
    (remember [_this ^js node memory]
      (.set ^js memories node memory))

    (recall [_this ^js node]
      (.get ^js memories node))))

(defonce ^:no-doc state (volatile! {}))

(defn ^:export render
  "Render `hiccup` in DOM element `el`. Replaces any pre-existing content not
  created by this function. Subsequent calls with the same `el` will update the
  rendered DOM by comparing `hiccup` to the previous `hiccup`. `hiccup` can be
  either a single hiccup node, or a list of multiple nodes."
  [^js el hiccup & [{:keys [aliases alias-data]}]]
  (let [rendering? (get-in @state [el :rendering?])]
    (when-not (contains? @state el)
      (set! (.-innerHTML el) "")
      (vswap! state assoc el {:renderer (create-renderer)
                              :unmounts (volatile! #{})
                              :unmount-hooks (volatile! (r/node-map))
                              :rendering? true}))
    (if rendering?
      (do
        (asserts/assert-no-nested-renders)
        (vswap! state assoc-in [el :queued] hiccup))
      (do
        (vswap! state assoc-in [el :rendering?] true)
        (let [{:keys [renderer current unmounts unmount-hooks]} (get @state el)
              aliases (or aliases (alias/get-registered-aliases))
              ;; with-dev-key is a clj macro tied to the cljs compiler, which
              ;; squint cannot expand, so the dev-key injection is skipped there
              hiccup #?(:squint hiccup
                        :default (if alias-data
                                   (env/with-dev-key hiccup [aliases alias-data])
                                   (env/with-dev-key hiccup aliases)))
              {:keys [vdom]} (try
                               (r/reconcile renderer el hiccup current {:unmounts unmounts
                                                                        :unmount-hooks unmount-hooks
                                                                        :aliases aliases
                                                                        :alias-data alias-data})
                               (catch :default e
                                 (js/console.error (str "Caught exception during rendering. "
                                                        (if aliases
                                                          "You may have misbehaving aliases, or you have encountered a bug in Replicant."
                                                          "This is likely a bug in Replicant."))
                                                   e)
                                 nil))]
          (vswap! state update el merge (cond-> {:rendering? false}
                                          vdom (assoc :current vdom)))
          (when-let [pending (:queued (get @state el))]
            (js/requestAnimationFrame #(render el pending))
            (vswap! state update el dissoc :queued))))))
  el)

(defn ^:export unmount
  "Unmounts elements in `el`, and clears internal state."
  [^js el]
  (if (get-in @state [el :rendering?])
    (js/requestAnimationFrame #(unmount el))
    (do
      (render el nil)
      (vswap! state dissoc el)
      nil)))

(defn ^:export set-dispatch!
  "Register a global dispatch function for event handlers and life-cycle hooks
  that are not functions. See data-driven event handlers and life-cycle hooks in
  the user guide for details."
  [f]
  (set! r/*dispatch* f))
